import BaseMapController from "./base_map_controller"
import { BaseMarker, MapBounds } from "@/utils/map_interfaces"
import { openMapPopover, MapPopover } from "@/utils/map_popover"
import { MarkerClusterer } from "@googlemaps/markerclusterer"
import { waitForMap } from "@/utils/map_utils"

interface OutreachMarker extends BaseMarker {
  detail_label?: string
  info: {
    occurred_at: string
    detail_path: string | null
    // Where the detail renders: the interaction opens in the drawer, a camp or
    // a help request is its own page. A link into a frame the page lacks is dead.
    detail_frame?: "drawer_frame" | "_top"
    neighbor_name?: string | null
    staff_name?: string | null
    program_name?: string | null
    status?: string | null
    description?: string | null
    population_estimate?: number | null
    structure_count?: number | null
  }
}

interface Layer {
  key: string
  label: string
  color: string
  markers: OutreachMarker[]
  total: number
  truncated: boolean
  ignored_filters: string[]
  detail_label: string
}

interface MarkerPayload {
  layers: Layer[]
  without_location: number
}

interface SurveyQuestion {
  id: number
  title: string
  answers: string[]
}

// Per layer, not per map: 200 camps and 200 contacts are two readable layers,
// but 200 pins of one kind is a blob.
const CLUSTER_THRESHOLD = 150

const FILTER_PARAMS = ["timeframe", "program_id", "interaction_tag_id", "gender", "veteran_status", "survey_id"]

// A survey answer filter is one param per pair: answers[<question id>]=<answer>.
const ANSWER_PARAM = /^answers\[(\d+)\]$/

const DEFAULT_LABELS: Record<string, string> = {
  showing: "Showing %{shown} of %{total} in view",
  capped: "%{layer} capped — zoom in or narrow the filters",
  without_location: "%{count} without a location, not shown",
  unfiltered: "%{count} · unfiltered",
  people: "%{count} people",
  structures: "%{count} structures",
  close: "Close"
}

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (char) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!
  )

// Mirrors OutreachMapQuery::DEFAULT_TIMEFRAME: the server applies it when the
// param is absent, so the panel shows it and the chips ignore it.
const DEFAULT_TIMEFRAME = "90d"

export default class extends BaseMapController {
  static targets = [
    ...BaseMapController.targets,
    "count", "status", "layerCount", "surveyFilter", "answerRows", "answerRowTemplate", "addAnswer", "logVisit",
    "filtersButton", "filterCount", "filterPanel", "panelFilter", "chipSection", "chipList", "chipTemplate"
  ]
  static values = {
    ...BaseMapController.values,
    detailLabel: String,
    // Translated copy for text the controller composes itself (status strip,
    // layer counts, popover detail); the view passes the locale's strings.
    labels: { type: Object, default: {} },
    surveyQuestions: { type: Object, default: {} },
    // The whole point of the page is to see the org's footprint, so open on the
    // data rather than on wherever the worker happens to be standing.
    useCurrentLocation: { type: Boolean, default: false }
  }

  declare readonly countTarget: HTMLElement
  declare readonly hasCountTarget: boolean
  declare readonly layerCountTargets: HTMLElement[]
  declare readonly statusTarget: HTMLElement
  declare readonly hasStatusTarget: boolean
  declare readonly surveyFilterTarget: HTMLSelectElement
  declare readonly answerRowsTarget: HTMLElement
  declare readonly answerRowTemplateTarget: HTMLTemplateElement
  declare readonly addAnswerTarget: HTMLElement
  declare readonly hasSurveyFilterTarget: boolean
  declare readonly logVisitTarget: HTMLAnchorElement
  declare readonly hasLogVisitTarget: boolean
  declare readonly filtersButtonTarget: HTMLButtonElement
  declare readonly hasFiltersButtonTarget: boolean
  declare readonly filterCountTarget: HTMLElement
  declare readonly filterPanelTarget: HTMLElement
  declare readonly hasFilterPanelTarget: boolean
  declare readonly panelFilterTargets: HTMLSelectElement[]
  declare readonly chipSectionTarget: HTMLElement
  declare readonly chipListTarget: HTMLElement
  declare readonly hasChipListTarget: boolean
  declare readonly chipTemplateTarget: HTMLTemplateElement
  declare readonly detailLabelValue: string
  declare readonly labelsValue: Record<string, string>
  declare readonly surveyQuestionsValue: Record<string, SurveyQuestion[]>

  private popover: MapPopover | null = null
  private layers: Layer[] = []
  // Layer visibility is a client concern: toggling must not refetch.
  private hidden = new Set<string>()
  private withoutLocation = 0
  private activeColor = "#287379"
  private activeGlyph = "outreach"
  private activeDetailLabel = ""
  // One clusterer per layer: clustering across layers would merge camps into
  // outreach counts, which is a different claim than "12 contacts here".
  private layerClusterers = new Map<string, MarkerClusterer>()
  // Monotonic token: a slow earlier request must not repaint over a newer one.
  private fetchSeq = 0
  // The filter panel too, so a restored page never comes back with it open.
  private beforeCache = () => {
    this.popover?.close()
    if (this.hasFilterPanelTarget) this.filterPanelTarget.dataset.open = "false"
  }
  private onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") this.closeFilters()
  }

  connect() {
    // Stimulus disconnect() runs after Turbo snapshots the body, so a popover
    // left open would be cached into the restored page with no live listeners
    // and no way to dismiss it.
    document.addEventListener("turbo:before-cache", this.beforeCache)
    document.addEventListener("keydown", this.onKeydown)
    // A shared URL arrives with filters already applied, so the answer rows,
    // the chips and the count have to describe them before the first fetch
    // returns.
    this.syncPanelFromParams()
    this.refreshChips()
    // The map is the whole page, so every touch belongs to it. The default
    // ("auto") turns cooperative on a phone: one finger scrolls the page and
    // panning needs two. Set as soon as the map exists, not after the first
    // marker fetch the base connect waits on.
    waitForMap(this.mapTarget).then(() => this.mapTarget.innerMap?.setOptions({ gestureHandling: "greedy" }))
    return super.connect()
  }

  protected async fetchMarkers(bounds?: MapBounds): Promise<void> {
    const seq = ++this.fetchSeq
    // A filter change or refetch invalidates whatever pin is open.
    this.popover?.close()
    this.popover = null

    const url = new URL(this.markersUrlValue, window.location.origin)

    if (bounds) {
      Object.entries(bounds).forEach(([key, value]) => {
        url.searchParams.set(key, value.toString())
      })
    }

    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (value && (FILTER_PARAMS.includes(key) || ANSWER_PARAM.test(key))) url.searchParams.set(key, value)
    })

    const response = await fetch(url.toString())
    if (!response.ok || seq !== this.fetchSeq) return

    const payload: MarkerPayload = await response.json()
    if (seq !== this.fetchSeq) return

    this.layers = payload.layers
    this.clearMarkers()

    const visible = payload.layers.filter((l) => !this.hidden.has(l.key))
    visible.forEach((layer) => this.renderLayer(layer))
    this.updateCount(payload)

    const shown = visible.flatMap((l) => l.markers)
    if (!bounds) this.fitMapToMarkers(shown)
  }

  // Goes through the base controller's renderMarkers so marker creation,
  // click wiring, the co-location jitter and the clusterer threshold stay in
  // one place. The base assigns this.markers, so layers are accumulated here.
  protected renderLayer(layer: Layer) {
    if (!this.map) return

    this.activeColor = layer.color
    this.activeGlyph = layer.key
    this.activeDetailLabel = layer.detail_label

    layer.markers.forEach((m) => { m.detail_label = layer.detail_label })

    const rendered = this.markers
    super.renderMarkers(layer.markers)
    const mine = this.markers
    this.markers = rendered.concat(mine)

    if (mine.length < CLUSTER_THRESHOLD) return

    // Above the threshold the pins stop being readable individually, so this
    // layer collapses into counts in its own colour.
    mine.forEach((marker) => marker.setMap(null))
    this.layerClusterers.set(
      layer.key,
      new MarkerClusterer({
        map: this.map!,
        markers: mine,
        algorithmOptions: { maxZoom: 16 },
        renderer: { render: ({ count, position }) => this.clusterMarker(position, count, layer.color) }
      })
    )
  }

  // Matches the shape, white centre and soft shadow of /marker-pin.png so the
  // combined map reads as the same product as the other maps; only the fill
  // changes per layer.
  protected createMarkerIcon<T extends BaseMarker>(_markerData: T): google.maps.Icon {
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="38" viewBox="0 0 30 38">` +
      `<defs><filter id="s" x="-40%" y="-20%" width="180%" height="160%">` +
      `<feDropShadow dx="0" dy="1.5" stdDeviation="1.4" flood-opacity="0.35"/></filter></defs>` +
      `<path filter="url(#s)" fill="${this.activeColor}" ` +
      `d="M15 1C7.8 1 2 6.8 2 14c0 7.2 8.1 15.1 11.6 18.3a2 2 0 0 0 2.8 0C19.9 29.1 28 21.2 28 14 28 6.8 22.2 1 15 1z"/>` +
      this.glyph() + `</svg>`

    return {
      url: `data:image/svg+xml;base64,${btoa(svg)}`,
      scaledSize: new google.maps.Size(30, 38),
      anchor: new google.maps.Point(15, 36)
    }
  }

  // Shape as well as colour: colour alone fails at small sizes and for
  // colour-blind users, and these are three different kinds of thing.
  private glyph(): string {
    switch (this.activeGlyph) {
      case "encampments": // a tent
        return `<path d="M15 8.2 20.4 18.6H9.6z" fill="#fff"/>`
      case "activity_reports": // a call for help
        return `<path d="M15 8v6.2" stroke="#fff" stroke-width="2.6" stroke-linecap="round"/>` +
               `<circle cx="15" cy="17.6" r="1.5" fill="#fff"/>`
      default: // a person contacted
        return `<circle cx="15" cy="13.6" r="5" fill="#fff"/>`
    }
  }

  private clusterMarker(position: google.maps.LatLng, count: number, color: string): google.maps.Marker {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">` +
      `<circle cx="20" cy="20" r="20" fill="${color}" fill-opacity="0.9"/></svg>`

    return new google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;base64,${btoa(svg)}`,
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16),
        labelOrigin: new google.maps.Point(16, 16)
      },
      label: { text: String(count), color: "white", fontSize: "12px", fontWeight: "bold", fontFamily: "Poppins, sans-serif" }
    })
  }

  // The base only knows about its own clusterer.
  protected clearMarkers() {
    this.layerClusterers.forEach((clusterer) => clusterer.setMap(null))
    this.layerClusterers.clear()
    super.clearMarkers()
  }

  // "Log visit" always points at whatever the map is currently looking at, so
  // the sheet opens with a pin instead of asking for one. `idle` rather than
  // `dragend`/`zoom_changed` (what the base listens to for the search button):
  // it is the one event that fires after every camera move, including the
  // programmatic `fitBounds` that sets the opening viewport.
  protected setupMapListeners() {
    super.setupMapListeners()
    if (!this.map || !this.hasLogVisitTarget) return

    google.maps.event.addListener(this.map, "idle", () => this.syncLogVisitHref())
    this.syncLogVisitHref()
  }

  private syncLogVisitHref() {
    const center = this.map?.getCenter()
    if (!center) return

    const url = new URL(this.logVisitTarget.href, window.location.origin)
    url.searchParams.set("latitude", center.lat().toFixed(6))
    url.searchParams.set("longitude", center.lng().toFixed(6))
    this.logVisitTarget.href = url.toString()
  }

  // Client-side only: hiding a layer must not cost a request.
  toggleLayer(event: Event) {
    const input = event.target as HTMLInputElement
    input.checked ? this.hidden.delete(input.value) : this.hidden.add(input.value)

    this.clearMarkers()
    this.layers.filter((l) => !this.hidden.has(l.key)).forEach((l) => this.renderLayer(l))
    this.refreshStatus()
  }

  protected handleMarkerClick<T extends BaseMarker>(markerData: T): () => void {
    const outreach = markerData as T & OutreachMarker

    return () => {
      if (!this.map) return

      // The base offsets co-located pins so they do not stack; anchor the
      // popover at the drawn position, not the raw one, or the tail misses.
      const drawn = this.offsetMarkerPosition(outreach)

      this.popover?.close()
      this.popover = openMapPopover(
        this.map,
        { lat: drawn.lat(), lng: drawn.lng() },
        this.buildPopoverContent(outreach),
        {
          anchor: this.element as HTMLElement,
          mapElement: this.mapTarget as unknown as HTMLElement
        }
      )
    }
  }

  // Plain app markup — DaisyUI classes, a real Turbo Frame link, our own close
  // affordance. None of this is expressible inside an InfoWindow. Layer-agnostic:
  // camps and help requests carry different fields to an outreach encounter.
  protected buildPopoverContent(marker: OutreachMarker): HTMLElement {
    const info = marker.info
    const byline = [info.occurred_at, info.staff_name, info.status]
      .filter(Boolean)
      .map((part) => escapeHtml(String(part)))
      .join(" &middot; ")

    const detail: string[] = []
    if (info.population_estimate != null) detail.push(this.label("people", { count: info.population_estimate }))
    if (info.structure_count != null) detail.push(this.label("structures", { count: info.structure_count }))
    if (info.description) detail.push(escapeHtml(info.description))

    const card = document.createElement("div")
    card.className = "card bg-base-100 shadow-lg border border-base-300 w-64 text-base-content"
    card.innerHTML = `
      <div class="card-body p-3 gap-1">
        <div class="flex items-start justify-between gap-2">
          <span class="font-semibold leading-tight">${escapeHtml(marker.title)}</span>
          <button type="button" data-popover-close
                  class="btn btn-ghost btn-xs btn-circle -mt-1 -mr-1" aria-label="${escapeHtml(this.label("close"))}">&times;</button>
        </div>
        <p class="text-xs text-base-content/60">${byline}</p>
        ${detail.length ? `<p class="text-xs text-base-content/70">${detail.join(" &middot; ")}</p>` : ""}
        ${info.program_name
          ? `<span class="badge badge-sm badge-ghost mt-1 self-start">${escapeHtml(info.program_name)}</span>`
          : ""}
        ${info.detail_path
          ? `<a href="${escapeHtml(info.detail_path)}" data-turbo-frame="${info.detail_frame || "_top"}"
               class="link link-primary text-sm font-medium mt-1">${escapeHtml(marker.detail_label || this.detailLabelValue)}</a>`
          : ""}
      </div>
      <div class="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 rotate-45
                  bg-base-100 border-r border-b border-base-300"></div>
    `
    return card
  }

  // The bar's own controls narrow immediately: they are visible on the map, so
  // there is nothing to stage and nothing to summarise.
  toggleFilters() {
    this.isFiltersOpen ? this.closeFilters() : this.openFilters()
  }

  openFilters() {
    if (!this.hasFilterPanelTarget) return

    this.filterPanelTarget.dataset.open = "true"
    this.setExpanded(true)
    // aria-modal is a promise that focus is inside the dialog; the close button
    // is the panel's first focusable, so Escape and Tab both start from there.
    // Without preventScroll, focusing it mid-slide (still parked off the right
    // edge) scrolls the page sideways to reveal it and shifts the whole map.
    this.filterPanelTarget.querySelector<HTMLElement>("button, select")?.focus({ preventScroll: true })
  }

  // Closing is abandoning: whatever was staged and not applied goes back to what
  // the map is actually showing, so reopening never lies about the filters.
  closeFilters() {
    if (!this.isFiltersOpen) return

    this.filterPanelTarget.dataset.open = "false"
    this.setExpanded(false)
    this.syncPanelFromParams()
    if (this.hasFiltersButtonTarget) this.filtersButtonTarget.focus()
  }

  // Panel selects only stage: they wait for Apply. A new survey starts its
  // question rows over, since the old survey's questions are not in it.
  stageFilter(event: Event) {
    if ((event.target as HTMLSelectElement).name === "survey_id") this.resetAnswerRows([])
  }

  addAnswerRow() {
    this.appendAnswerRow()
  }

  // Both row actions are bound on the rows' container, not on each cloned
  // row: Stimulus wires a new element's own actions a tick after it is
  // inserted, and a pick made inside that tick would be lost.
  removeAnswerRow(event: Event) {
    (event.target as HTMLElement).closest("[data-answer-remove]")?.closest("[data-answer-row]")?.remove()
  }

  // The question picked in a row decides which answers that row offers.
  stageQuestion(event: Event) {
    const question = event.target as HTMLSelectElement
    if (!question.matches("[data-answer-question]")) return

    const answer = question.closest("[data-answer-row]")!.querySelector<HTMLSelectElement>("[data-answer-value]")!
    this.repopulate(answer, this.answerOptions(question.value))
  }

  applyFilters() {
    this.panelFilterTargets.forEach((select) => this.setParam(select.name, select.value))
    this.setAnswerParams(this.stagedAnswers())
    this.closeFilters()
    this.commit()
  }

  clearFilters() {
    this.panelFilterTargets.forEach((select) => { select.value = select.name === "timeframe" ? DEFAULT_TIMEFRAME : "" })
    this.resetAnswerRows([])
    this.applyFilters()
  }

  removeFilter(event: Event) {
    const chip = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-filter]")
    if (!chip?.dataset.filter) return

    const name = chip.dataset.filter
    const select = this.panelSelect(name)
    if (select) select.value = ""
    this.setParam(name, "")
    // The survey owns its answers, so its chip takes them with it.
    if (name === "survey_id") this.setAnswerParams([])
    if (name === "survey_id" || ANSWER_PARAM.test(name)) this.resetAnswerRows(this.appliedAnswers())

    this.commit()
  }

  private commit() {
    this.refreshChips()
    this.fetchMarkersForCurrentBounds()
  }

  private get isFiltersOpen(): boolean {
    return this.hasFilterPanelTarget && this.filterPanelTarget.dataset.open === "true"
  }

  private setExpanded(open: boolean) {
    if (this.hasFiltersButtonTarget) this.filtersButtonTarget.setAttribute("aria-expanded", String(open))
  }

  private panelSelect(name: string): HTMLSelectElement | undefined {
    return this.panelFilterTargets.find((select) => select.name === name)
  }

  // Selects first: the answer rows can only be rebuilt from the survey that is
  // actually applied.
  private syncPanelFromParams() {
    const params = new URLSearchParams(window.location.search)
    this.panelFilterTargets.forEach((select) => {
      select.value = params.get(select.name) || (select.name === "timeframe" ? DEFAULT_TIMEFRAME : "")
    })
    this.resetAnswerRows(this.appliedAnswers())
  }

  // The question/answer pairs the map is narrowed by, as [question id, answer].
  private appliedAnswers(): Array<[string, string]> {
    const pairs: Array<[string, string]> = []
    new URLSearchParams(window.location.search).forEach((value, key) => {
      const id = key.match(ANSWER_PARAM)?.[1]
      if (id && value) pairs.push([id, value])
    })
    return pairs
  }

  // Only complete rows count: a question with no answer narrows nothing.
  private stagedAnswers(): Array<[string, string]> {
    if (!this.hasSurveyFilterTarget) return []

    return Array.from(this.answerRowsTarget.querySelectorAll("[data-answer-row]"))
      .map((row): [string, string] => [
        row.querySelector<HTMLSelectElement>("[data-answer-question]")!.value,
        row.querySelector<HTMLSelectElement>("[data-answer-value]")!.value
      ])
      .filter(([question, answer]) => question && answer)
  }

  private setAnswerParams(pairs: Array<[string, string]>) {
    const url = new URL(window.location.href)

    Array.from(url.searchParams.keys()).filter((key) => ANSWER_PARAM.test(key)).forEach((key) => url.searchParams.delete(key))
    pairs.forEach(([question, answer]) => url.searchParams.set(`answers[${question}]`, answer))
    window.history.replaceState({}, "", url.toString())
  }

  // A staged survey always shows one row to fill in.
  private resetAnswerRows(pairs: Array<[string, string]>) {
    if (!this.hasSurveyFilterTarget) return

    // No survey staged, or one with no choice questions: nothing to pick from.
    const answerable = this.questionOptions().length > 0
    this.answerRowsTarget.replaceChildren()
    this.addAnswerTarget.classList.toggle("hidden", !answerable)
    if (!answerable) return

    const rows: Array<[string, string]> = pairs.length > 0 ? pairs : [["", ""]]
    rows.forEach(([question, answer]) => this.appendAnswerRow(question, answer))
  }

  private appendAnswerRow(questionId = "", answer = "") {
    const row = this.answerRowTemplateTarget.content.firstElementChild!.cloneNode(true) as HTMLElement
    const question = row.querySelector<HTMLSelectElement>("[data-answer-question]")!
    const value = row.querySelector<HTMLSelectElement>("[data-answer-value]")!

    this.repopulate(question, this.questionOptions())
    question.value = questionId
    this.repopulate(value, this.answerOptions(questionId))
    value.value = answer
    this.answerRowsTarget.append(row)
  }

  // One chip per filter that is actually narrowing the map, so the count on the
  // button is a promise the map keeps.
  private appliedChips(): Array<{ param: string; label: string }> {
    const params = new URLSearchParams(window.location.search)
    const chips: Array<{ param: string; label: string }> = []

    // The option the server rendered is already the human-readable name, so a
    // chip never has to reproduce a program or survey title of its own.
    for (const param of ["timeframe", "program_id", "interaction_tag_id", "gender", "survey_id"]) {
      const value = params.get(param)
      // The default timeframe is what the map shows anyway, so it is not a chip.
      if (param === "timeframe" && (!value || value === DEFAULT_TIMEFRAME)) continue
      const label = Array.from(this.panelSelect(param)?.options || [])
        .find((option) => option.value === value)?.text

      if (value && label) chips.push({ param, label })
    }

    // Read the titles from the survey data rather than the rows: the rows
    // follow whatever is staged, the params follow what is applied.
    const questions = this.surveyQuestionsValue[params.get("survey_id") || ""] || []
    this.appliedAnswers().forEach(([id, answer]) => {
      const question = questions.find((q) => String(q.id) === id)
      if (question) chips.push({ param: `answers[${id}]`, label: `${question.title} = ${answer}` })
    })

    return chips
  }

  private refreshChips() {
    const chips = this.appliedChips()

    if (this.hasChipListTarget) {
      this.chipListTarget.replaceChildren(...chips.map((chip) => this.buildChip(chip)))
      this.chipSectionTarget.classList.toggle("hidden", chips.length === 0)
    }

    if (!this.hasFiltersButtonTarget) return

    this.filterCountTarget.textContent = String(chips.length)
    this.filterCountTarget.classList.toggle("hidden", chips.length === 0)
    this.filtersButtonTarget.classList.toggle("filters-active", chips.length > 0)
  }

  private buildChip(chip: { param: string; label: string }): HTMLElement {
    const node = this.chipTemplateTarget.content.firstElementChild!.cloneNode(true) as HTMLElement

    node.dataset.filter = chip.param
    node.querySelector("[data-chip-label]")!.textContent = chip.label
    return node
  }

  private setParam(name: string, value: string) {
    const url = new URL(window.location.href)

    if (value) {
      url.searchParams.set(name, value)
    } else {
      url.searchParams.delete(name)
    }
    window.history.replaceState({}, "", url.toString())
  }

  private questionOptions(): Array<[string, string]> {
    const questions = this.surveyQuestionsValue[this.surveyFilterTarget.value] || []
    return questions.map((question) => [String(question.id), question.title])
  }

  private answerOptions(questionId: string): Array<[string, string]> {
    const questions = this.surveyQuestionsValue[this.surveyFilterTarget.value] || []
    const selected = questions.find((question) => String(question.id) === questionId)
    return (selected?.answers || []).map((answer) => [answer, answer])
  }

  // Rebuilds a dependent select and drops the choice it was carrying: a question
  // from the previous survey is not a valid narrowing of this one. A select with
  // nothing to offer is hidden rather than shown empty. DOM only — the params
  // are written by whoever commits.
  private repopulate(select: HTMLSelectElement, options: Array<[string, string]>) {
    const [blank] = Array.from(select.options)

    select.replaceChildren(blank, ...options.map(([value, label]) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label
      return option
    }))
    select.value = ""
    // `!hidden`, not the hidden property: DaisyUI's .select sets its own display.
    select.classList.toggle("!hidden", options.length === 0)
  }

  protected fitMapToMarkers(markers: OutreachMarker[]) {
    if (!this.map || markers.length === 0) return

    const bounds = new google.maps.LatLngBounds()
    markers.forEach((marker) => bounds.extend({ lat: marker.latitude, lng: marker.longitude }))

    this.map.fitBounds(bounds)
    if (markers.length === 1) this.map.setZoom(Math.min(15, this.map.getZoom() || 15))
  }

  protected updateCount(payload: MarkerPayload) {
    this.withoutLocation = payload.without_location
    this.refreshStatus()
  }

  // Counts follow layer visibility, so the number always describes the dots
  // actually on screen.
  // The layer panel carries each layer's own count, so a filtered layer sitting
  // next to an unfiltered one is visible rather than implied.
  protected refreshLayerPanel() {
    this.layerCountTargets.forEach((el) => {
      const layer = this.layers.find((l) => l.key === el.dataset.layerKey)
      if (!layer) return

      const ignored = layer.ignored_filters.length
      el.textContent = ignored > 0 ? this.label("unfiltered", { count: layer.total }) : String(layer.total)
      el.classList.toggle("text-amber-700", ignored > 0)
    })
  }

  protected refreshStatus() {
    this.refreshLayerPanel()
    const visible = this.layers.filter((l) => !this.hidden.has(l.key))
    const total = visible.reduce((n, l) => n + l.total, 0)
    const shown = visible.reduce((n, l) => n + l.markers.length, 0)
    const capped = visible.filter((l) => l.truncated)

    if (this.hasCountTarget) {
      this.countTarget.textContent = total.toString()
      this.countTarget.classList.remove("!hidden")
    }

    if (!this.hasStatusTarget) return

    // Say what the map is not showing. A capped or incomplete map looks
    // identical to a complete one.
    const notes: string[] = [this.label("showing", { shown, total })]
    capped.forEach((l) => notes.push(this.label("capped", { layer: l.label })))
    if (this.withoutLocation > 0) notes.push(this.label("without_location", { count: this.withoutLocation }))

    const truncated = capped.length > 0
    const without_location = this.withoutLocation
    this.statusTarget.textContent = notes.join(" · ")
    // DaisyUI's `text-warning` is a mid yellow and unreadable on the white
    // strip; amber-700 keeps the caveat legible without shouting.
    this.statusTarget.classList.toggle("text-amber-700", truncated || without_location > 0)
    this.statusTarget.classList.toggle("font-medium", truncated || without_location > 0)
  }

  // Rails-style %{name} interpolation over the view's strings; the English
  // fallbacks keep the controller usable without a labels value.
  private label(key: string, vars: Record<string, string | number> = {}): string {
    const template = this.labelsValue[key] || DEFAULT_LABELS[key] || key
    return template.replace(/%\{(\w+)\}/g, (_, name) => String(vars[name] ?? ""))
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    document.removeEventListener("keydown", this.onKeydown)
    this.fetchSeq++
    this.popover?.close()
    this.popover = null
    super.disconnect()
  }
}
